import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Home, 
  CreditCard, 
  BarChart3, 
  Target, 
  MessageCircle, 
  Settings,
  LogOut,
  Menu,
  X,
  Wallet,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavbar } from '../../context/NavbarContext';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { UserAvatar } from '../ui/UserAvatar';
import {
  StyledNavigationContainer,
  StyledMobileHeader,
  StyledMobileMenuButton,
  StyledNavigationOverlay,
  StyledNavigation,
  StyledMobileCloseButton,
  StyledLogo,
  StyledLogoText,
  StyledMobileLogo,
  StyledNavList,
  StyledNavItem,
  StyledNavLink,
  StyledBottomSection,
  StyledUserInfoSection,
  StyledUserDetails,
  StyledUserName,
  StyledUserEmail,
  StyledSeparator,
  StyledLanguageSwitcherContainer,
  StyledLogoutButton,
  StyledCollapseButton
} from './Navigation.styles';

const Navigation: React.FC = () => {
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const { isCollapsed, toggleCollapse } = useNavbar();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    {
      path: '/dashboard',
      icon: Home,
      label: t('navigation.dashboard'),
    },
    {
      path: '/transactions',
      icon: CreditCard,
      label: t('navigation.transactions'),
    },
    {
      path: '/cards',
      icon: Wallet,
      label: t('navigation.cards'),
    },
    {
      path: '/analytics',
      icon: BarChart3,
      label: t('navigation.analytics'),
    },
    {
      path: '/goals',
      icon: Target,
      label: t('navigation.goals'),
    },
    {
      path: '/chat',
      icon: MessageCircle,
      label: t('navigation.chat'),
    },
    {
      path: '/settings',
      icon: Settings,
      label: t('navigation.settings'),
    },
  ];

  const openMobileMenu = () => {
    setIsMobileMenuOpen(true);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Close mobile menu on desktop resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <StyledMobileHeader>
        <StyledMobileLogo>FinanceGrow</StyledMobileLogo>
        <StyledMobileMenuButton 
          onClick={openMobileMenu}
          aria-label="Open navigation menu"
        >
          <Menu />
        </StyledMobileMenuButton>
      </StyledMobileHeader>

      <StyledNavigationOverlay 
        $isOpen={isMobileMenuOpen} 
        onClick={closeMobileMenu}
      />

      <StyledNavigationContainer $isCollapsed={isCollapsed}>
        <StyledNavigation $isOpen={isMobileMenuOpen} $isCollapsed={isCollapsed}>
          <StyledCollapseButton 
            onClick={toggleCollapse}
            aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
          >
            {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </StyledCollapseButton>

          <StyledMobileCloseButton 
            onClick={closeMobileMenu}
            aria-label="Close navigation menu"
          >
            <X />
          </StyledMobileCloseButton>

          <StyledLogo $isCollapsed={isCollapsed}>
            <StyledLogoText $isCollapsed={isCollapsed}>
              {isCollapsed ? 'FG' : 'FinanceGrow'}
            </StyledLogoText>
          </StyledLogo>

          <StyledNavList>
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <StyledNavItem key={item.path}>
                  <StyledNavLink 
                    to={item.path} 
                    $isCollapsed={isCollapsed}
                    onClick={closeMobileMenu}
                  >
                    <Icon />
                    <span className="nav-text">{item.label}</span>
                  </StyledNavLink>
                </StyledNavItem>
              );
            })}
          </StyledNavList>

          <StyledBottomSection>
            <StyledUserInfoSection $isCollapsed={isCollapsed}>
              <UserAvatar 
                firstName={user?.firstName || ''} 
                lastName={user?.lastName || ''} 
                size="sm"
              />
              <StyledUserDetails className="user-details">
                <StyledUserName>
                  {user?.firstName && user?.lastName 
                    ? `${user.firstName} ${user.lastName}`
                    : user?.email?.split('@')[0] || 'User'
                  }
                </StyledUserName>
                <StyledUserEmail>{user?.email || ''}</StyledUserEmail>
              </StyledUserDetails>
            </StyledUserInfoSection>
            
            <StyledSeparator />
            
            <StyledLanguageSwitcherContainer $isCollapsed={isCollapsed}>
              <LanguageSwitcher />
            </StyledLanguageSwitcherContainer>

            <StyledLogoutButton 
              onClick={handleLogout} 
              $isCollapsed={isCollapsed}
              aria-label="Logout"
            >
              <LogOut />
              <span className="logout-text">{t('auth.logout')}</span>
            </StyledLogoutButton>
          </StyledBottomSection>
        </StyledNavigation>
      </StyledNavigationContainer>
    </>
  );
};

export default Navigation;
